import { FC, AnchorHTMLAttributes } from 'react';
import { BodyText, Icon } from '@servicetitan/design-system';
import { tokens } from '@servicetitan/tokens/core';
import AuthModule from '@modules/AuthModule';

export interface MonolithLinkProps
  extends Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'target' | 'rel' | 'onAuxClick'
  > {
  href: string;
  children: React.ReactNode;
  hideLinkOutIcon?: boolean;
  bold?: boolean;
  onAuxClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

/**
 * Link component for navigating to the ServiceTitan monolith.
 * Automatically handles auth session extension by calling resetAuthAge().
 * Renders an external-link icon by default because the link always opens in a
 * new tab; set `hideLinkOutIcon` to opt out.
 */
export const MonolithLink: FC<MonolithLinkProps> = ({
  href,
  children,
  hideLinkOutIcon = false,
  className,
  bold,
  onClick,
  onAuxClick,
  ...props
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    AuthModule.resetAuthAge();
    onClick?.(e);
  };

  // Handle middle-click (auxclick event)
  const handleAuxClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    AuthModule.resetAuthAge();
    onAuxClick?.(e);
  };

  return (
    <BodyText
      el='a'
      {...props}
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={className}
      bold={bold}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {children}
      {!hideLinkOutIcon && <Icon name='open_in_new' color={tokens.colorBlue} />}
    </BodyText>
  );
};
